// cSpell:ignore afgerekend afrekening afrekeningen alleen aparte betaaldetails betaalstatus betaaluitnodigingen Betalingen bevestigde concepten Deels deze Gebouw gebouw Geen huurder lijst niet opent Opgenomen opgeslagen pagina Panden Privaat Scenariolaan toont verdeelsleutel vertrek voor waarop waarvan Wijzigen zelfde
/* eslint-disable no-nested-ternary */
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Card, EmptyState, Stack } from "@rentiohq/web-shared/dist/components";
import { isEqual } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import {
  buildingPageCosts,
  costLevelOf,
  costListTotals,
  costRowAccess,
  costRowLevel,
  unitPageCosts,
} from "../costLevels.utils";
import { costsHaveOpenPayment } from "../costPayment.utils";
import {
  costSettledInfo,
  costSettlementStateOf,
  costSettlementStates,
} from "../costSettlementState.utils";
import {
  getListState,
  groupCostPartForUnit,
  isPageOwnSettlement,
  listedSettlementsOf,
  openPaymentMessage,
  settlementDeleteBlockedMessage,
} from "../costsList.utils";
import { departureCandidates } from "../departure.utils";
import {
  groupCostAppliesToUnit,
  groupCostUnitShare,
  TGroupCostUnitShare,
} from "../groupCostShare.utils";
import {
  unitAddressLabel,
  unitBoxLabel,
  unitFullAddress,
  unitParentGroupId,
} from "../groupUnits.utils";
import {
  settlementTenantPaymentStatus,
  useAssignableProperties,
  useCanEditCosts,
  useCanEditCostsOf,
  useCostPaymentStatusMaps,
  useCostsEditPermission,
  useGroupUnits,
  useInvoices,
  useKeys,
  useParentGroupInvoices,
  usePropertyRentalContracts,
  useSettlementPaymentStatus,
  useSettlements,
  deleteRefusedMessage,
} from "../hooks";
import { todayIsoDay } from "../localDay.utils";
import { formatMoney } from "../moneyText.utils";
import {
  settlementAmountTexts,
  settlementRowLevel,
  settlementRowTexts,
} from "../settlementListColumns.utils";
import { paymentRequestPropertyIds } from "../settlementPaymentStatus.utils";
import {
  CATEGORY_LABELS_NL,
  Invoice,
  PaymentSplit,
  Settlement,
} from "../types";
import { useWithArchivedProperties } from "../useArchivedProperties";
import { CostsTable, ICostsTableView } from "./CostsTable";
import { InvoiceModal } from "./InvoiceModal";
import { ListLoadFailed } from "./ListLoadFailed";
import { NewSettlementModal } from "./NewSettlementModal";
import { SettlementRowVM, SettlementsTable } from "./SettlementsTable";

export interface CostsTabProps {
  scope: "group" | "unit";
  groupId?: string;
  unitId?: string;
  // An archived property's costs and settlements stay readable, but nothing
  // is added or changed until it is activated again.
  isArchived?: boolean;
  // The group of this page's own property, from its own record: it stays
  // known once the unit is archived (see unitParentGroupId).
  propertyParentId?: number;
}

export const CostsTab: React.FC<CostsTabProps> = ({
  scope,
  groupId,
  unitId,
  isArchived = false,
  propertyParentId,
}) => {
  const navigate = useNavigate();
  const pageEditPermission = useCostsEditPermission(
    scope === "group" ? groupId : unitId,
  );
  const canEditProperty = pageEditPermission.canEdit;
  const canEdit = canEditProperty && !isArchived;
  const { items: activeProperties, isFetching: isFetchingProperties } =
    useAssignableProperties();
  // The property list leaves archived properties out: this page's own
  // property, when archived, is added so it keeps its name and its group.
  const assignable = useWithArchivedProperties({
    properties: activeProperties,
    isFetching: isFetchingProperties,
    ids: [unitId, groupId],
  });
  // Group-cost children live on the group's units (propertyId=unitId), not on
  // the group id itself. To fetch them for the group-scope list we need the
  // unit ids alongside the group id. All of its units, archived ones too: a
  // unit archived since keeps its legacy group-cost children, its own
  // settlements and its tenants' payment requests, which the property list
  // (assignable) leaves out.
  const { units: groupUnits } = useGroupUnits(
    scope === "group" ? groupId : undefined,
  );
  const groupChildIds = React.useMemo(
    () => groupUnits.map(unit => String(unit.id)),
    [groupUnits],
  );
  const filter =
    scope === "group" ? { groupId, unitIds: groupChildIds } : { unitId };
  const {
    items: fetchedInvoices,
    isLoading: isLoadingInvoices,
    loadFailed: invoicesLoadFailed,
    create,
    update,
    remove,
    refetch,
  } = useInvoices(filter);
  // One cost list in blocks (design 2026-10-16, B and A): the building page
  // lists the building's costs and every unit's own costs, each once (see
  // buildingPageCosts); the unit page its own costs and its building's (see
  // costRows below). The ledgers underneath stay separate.
  const invoices = fetchedInvoices;
  // An archived unit keeps its group: its former tenants were billed by the
  // group's settlements and costs, which its tab still lists.
  const parentGroupId = unitParentGroupId({
    unitId: scope === "unit" ? unitId : undefined,
    ownParentId: propertyParentId,
    properties: assignable,
  });
  // On a unit inside a group: the group's costs, with this unit's share of
  // each (see groupCostRows), its keys and its units to work that share out.
  const parentGroupInvoices = useParentGroupInvoices(
    scope === "unit" ? parentGroupId : undefined,
  );
  const { units: parentGroupUnits } = useGroupUnits(
    scope === "unit" ? parentGroupId : undefined,
  );
  const { items: parentGroupKeys } = useKeys(
    scope === "unit" ? parentGroupId ?? "" : "",
  );
  // Live payment status for the cost list — the tenant payment requests live on
  // the unit (unit scope) or the group's child units (group scope). Bulk
  // payment creation on a group lands its requests on the group property
  // itself, so include the group id as well.
  const paymentPropertyIds = React.useMemo(
    () =>
      (scope === "group" ? [groupId, ...groupChildIds] : unitId ? [unitId] : [])
        .map(Number)
        .filter(n => !Number.isNaN(n)),
    [scope, groupId, groupChildIds, unitId],
  );
  const costsWithPayments = React.useMemo(
    () => [...invoices, ...parentGroupInvoices],
    [invoices, parentGroupInvoices],
  );
  const costStatusMaps = useCostPaymentStatusMaps(
    costsWithPayments,
    paymentPropertyIds,
  );
  const costStatusByOrderId = costStatusMaps.byOrderId;
  const {
    items: settlements,
    isLoading: isLoadingSettlements,
    loadFailed: ownSettlementsLoadFailed,
    remove: removeSettlement,
    refetch: refetchSettlements,
    // Each level lists its own settlements: the group's settlements cover its
    // group costs, a unit's settlements cover its own costs.
  } = useSettlements(scope === "group" ? { groupId } : { unitId });
  // The "settled" state of a cost looks across levels: a legacy group-cost
  // child lives on a unit but may have been settled on the group, and vice
  // versa. The list above stays per level; this one only feeds the cost states.
  const {
    items: crossLevelSettlements,
    loadFailed: crossLevelSettlementsLoadFailed,
    refetch: refetchCrossLevelSettlements,
  } = useSettlements(
    scope === "group"
      ? { groupId, unitIds: groupChildIds }
      : { unitId, unitIds: parentGroupId ? [parentGroupId] : [] },
  );
  const settlementsLoadFailed =
    ownSettlementsLoadFailed || crossLevelSettlementsLoadFailed;
  const handleRetrySettlements = () => {
    refetchSettlements();
    refetchCrossLevelSettlements();
  };

  const [showModal, setShowModal] = useState(false);
  const [showNewSettlement, setShowNewSettlement] = useState(false);
  // The unit's leases that can still get a departure settlement.
  const unitLeases = usePropertyRentalContracts(
    scope === "unit" && unitId ? Number(unitId) : undefined,
  );
  const departureLeases = React.useMemo(
    () =>
      scope === "unit"
        ? departureCandidates({
            contracts: unitLeases.map(rental => rental.ref),
            settlements,
            today: todayIsoDay(),
          })
        : [],
    [scope, unitLeases, settlements],
  );
  const startSettlement = (departureContractId?: string) => {
    const base =
      scope === "group" ? `/properties/${groupId}` : `/properties/${unitId}`;
    navigate(
      `${base}/costs/settlements/add${
        departureContractId ? `?departure=${departureContractId}` : ""
      }`,
    );
  };
  // A unit inside a building first asks which settlement: the building's
  // (made on the building, for a user who may settle its costs), the unit's
  // own interim one, or a departure (design 2026-10-16, C).
  const canSettleParentGroup = useCanEditCosts(
    scope === "unit" ? parentGroupId : undefined,
  );
  const unitProperty =
    scope === "unit" ? assignable.find(p => p.id === unitId) : undefined;
  const unitLabelInBuilding =
    scope === "unit" && parentGroupId
      ? unitProperty
        ? unitBoxLabel(unitProperty)
        : unitId ?? ""
      : undefined;
  const startBuildingSettlement =
    unitLabelInBuilding != null && canSettleParentGroup
      ? () => navigate(`/properties/${parentGroupId}/costs/settlements/add`)
      : undefined;
  // What the cost list shows after its search and filters (its heading).
  const [costsView, setCostsView] = useState<ICostsTableView | null>(null);
  const handleCostsViewChange = (view: ICostsTableView) =>
    setCostsView(prev => (isEqual(prev, view) ? prev : view));
  const [drawerParams, setDrawerParams] = useQueryParams({
    propertyCostId: StringParam,
    propertyCostEditId: StringParam,
    settlementId: StringParam,
  });
  const openCostDrawer = (inv: Invoice) =>
    setDrawerParams({ propertyCostId: inv.id });
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);

  // The detail drawer (PropertyCost) updates via propertyCostPromises.update which
  // only writes to Redux detail[id], not the paged slice. Refetch the list when
  // the drawer closes so split-paid status changes appear immediately in the table.
  const prevCostIdRef = useRef(drawerParams.propertyCostId);
  useEffect(() => {
    const prev = prevCostIdRef.current;
    prevCostIdRef.current = drawerParams.propertyCostId;
    if (prev && !drawerParams.propertyCostId) {
      refetch();
    }
  }, [drawerParams.propertyCostId, refetch]);

  // Same pattern for the settlement drawer: refresh the list once it closes
  // (or after a delete from inside the drawer).
  const prevSettlementIdRef = useRef(drawerParams.settlementId);
  useEffect(() => {
    const prev = prevSettlementIdRef.current;
    prevSettlementIdRef.current = drawerParams.settlementId;
    if (prev && !drawerParams.settlementId) {
      refetchSettlements();
    }
  }, [drawerParams.settlementId, refetchSettlements]);

  // The building the page's costs belong to: the page's own on a building,
  // the parent on a unit inside one. A unit on its own has none.
  const buildingId = scope === "group" ? groupId : parentGroupId;
  const levelOf = (inv: Invoice) => costLevelOf({ invoice: inv, buildingId });
  // A unit of the building as its costs name it: "bus 2", else its name
  // (unitBoxLabel); an archived unit, which the property list leaves out,
  // by its label among the building's units.
  const unitLabelOf = (id?: string) => {
    const property = assignable.find(p => p.id === id);
    if (property) return unitBoxLabel(property);
    const unit = [...groupUnits, ...parentGroupUnits].find(
      u => String(u.id) === id,
    );
    return unit?.label ?? id ?? getLocalizedText("costs.scope.private");
  };
  // A unit's full address with its box spelled out, "Scenariolaan 1 bus 1,
  // 9000 Gent", as the summary and the cost choice write it
  // (unitAddressLabel); a unit the property list does not hold as the
  // building's units write it (see unitFullAddress).
  const unitAddressOf = (id?: string) => {
    const property = assignable.find(p => p.id === id);
    if (property) return unitAddressLabel(property);
    return unitFullAddress({
      unitId: id,
      units: [...groupUnits, ...parentGroupUnits],
      properties: assignable,
    });
  };
  // Inside a building every row carries "Gebouw" or "Privaat" in its Type
  // column, a private cost's unit by its full address in the tooltip of
  // "Privaat" (design 2026-10-16, D and E; Lewis, 2026-10-16: no line under
  // it); a unit on its own has no Type column.
  const rowLevelOf = (inv: Invoice) =>
    costRowLevel({
      invoice: inv,
      buildingId,
      page: scope,
      unitLabelOf,
      unitAddressOf,
    });
  // Which costs a confirmed settlement settled (→ "Opgenomen in afrekening"),
  // and which a departure settlement settled part of (→ "Deels afgerekend
  // (vertrek)"). The group page reads its units' departures too.
  const settlementStates = React.useMemo(
    () => costSettlementStates(crossLevelSettlements),
    [crossLevelSettlements],
  );
  const settledInfoFor = (inv: Invoice) =>
    costSettledInfo(costSettlementStateOf(settlementStates, inv.id));

  // On a unit inside a building: every cost of the building, read-only, with
  // this unit's share of it (see groupCostShare.utils). They are made and
  // changed on the building; the unit's list labels them "Gebouw" and counts
  // them at that share (Q3). A row
  // carries the status of this unit's tenants: their payments of a cost
  // billed right away, or the owner's when the cost asked none of them.
  const groupCostRows = React.useMemo(() => {
    if (scope !== "unit" || !parentGroupId) return [];
    // A split is this unit's when it names the unit. One that does not (from
    // before splits stored their property) is when one of the unit's own
    // payment requests carries it: the request id is exact, the order id the
    // fallback. A bulk request on the group lives on the group, not the unit.
    const isUnitPayment = (split: PaymentSplit) => {
      if (split.propertyId != null) return String(split.propertyId) === unitId;
      return split.paymentRequestId != null
        ? costStatusMaps.byRequestId[split.paymentRequestId] != null
        : split.paymentOrderId != null &&
            costStatusMaps.byOrderId[Number(split.paymentOrderId)] != null;
    };
    return parentGroupInvoices.map(invoice => {
      const isBilled = (invoice.paymentSplits ?? []).length > 0;
      const unitPart = groupCostPartForUnit({ invoice, isUnitPayment });
      const statusRow: Invoice =
        unitPart ??
        (isBilled
          ? {
              ...invoice,
              target: "owner",
              tenantSharePct: undefined,
              paymentSplits: [],
            }
          : invoice);
      // The cost's whole amount: the share column prints it as "of …".
      return { ...statusRow, amountExclVat: invoice.amountExclVat };
    });
  }, [scope, parentGroupId, parentGroupInvoices, costStatusMaps, unitId]);

  // The confirmed settlement of the group that settled each group cost.
  const groupSettlementByCostId = React.useMemo(() => {
    const byCostId = new Map<string, Settlement>();
    for (const settlement of crossLevelSettlements) {
      if (
        settlement.status !== "confirmed" ||
        settlement.scope !== "group" ||
        settlement.groupId !== parentGroupId ||
        !settlement.snapshot
      ) {
        continue;
      }
      for (const id of settlement.snapshot.invoiceIds) {
        byCostId.set(id, settlement);
      }
    }
    return byCostId;
  }, [crossLevelSettlements, parentGroupId]);

  const parentGroupCostIds = React.useMemo(
    () => new Set(parentGroupInvoices.map(cost => cost.id)),
    [parentGroupInvoices],
  );
  // The unit's share of each of its building's costs, worked out once.
  const parentGroupShares = React.useMemo(() => {
    const shares = new Map<string, TGroupCostUnitShare>();
    if (scope !== "unit") return shares;
    for (const invoice of parentGroupInvoices) {
      shares.set(
        invoice.id,
        groupCostUnitShare({
          invoice,
          unitId: unitId ?? "",
          settlement: groupSettlementByCostId.get(invoice.id),
          groupInvoices: parentGroupInvoices,
          keys: parentGroupKeys,
          groupUnits: parentGroupUnits,
        }),
      );
    }
    return shares;
  }, [
    scope,
    unitId,
    parentGroupInvoices,
    groupSettlementByCostId,
    parentGroupKeys,
    parentGroupUnits,
  ]);
  // The unit's share of a building's cost on the unit page; a row of the
  // page's own shows its own amount.
  const shareFor = (row: Invoice) =>
    scope === "unit" ? parentGroupShares.get(row.id) : undefined;

  // The building's costs that apply to the unit (see groupCostAppliesToUnit):
  // the unit page leaves out a share of € 0,00 and a split still to be made
  // whose key the building's draft settlement chose leaves the unit out.
  const parentGroupSettlements = React.useMemo(
    () =>
      crossLevelSettlements.filter(
        settlement =>
          settlement.scope === "group" && settlement.groupId === parentGroupId,
      ),
    [crossLevelSettlements, parentGroupId],
  );
  const groupCostAppliesToPageUnit = React.useCallback(
    (row: Invoice) => {
      const share = parentGroupShares.get(row.id);
      const invoice = parentGroupInvoices.find(cost => cost.id === row.id);
      if (!share || !invoice) return true;
      return groupCostAppliesToUnit({
        share,
        invoice,
        unitId: unitId ?? "",
        keys: parentGroupKeys,
        groupUnits: parentGroupUnits,
        groupSettlements: parentGroupSettlements,
      });
    },
    [
      parentGroupShares,
      parentGroupInvoices,
      unitId,
      parentGroupKeys,
      parentGroupUnits,
      parentGroupSettlements,
    ],
  );

  // The rows of the cost list: the building's and its units' on a building
  // (a legacy group cost once, every one of them), the unit's own and the
  // building's that apply to it on a unit.
  const costRows = React.useMemo(
    () =>
      scope === "group"
        ? buildingPageCosts({ invoices, buildingId: groupId })
        : unitPageCosts({
            ownInvoices: invoices,
            buildingInvoices: groupCostRows,
            appliesToUnit: groupCostAppliesToPageUnit,
          }),
    [scope, invoices, groupId, groupCostRows, groupCostAppliesToPageUnit],
  );
  // Count and total of the rows the list shows, at the unit's share for a
  // building's cost (Q3: a share still to be worked out adds nothing and is
  // named).
  const costTotals = costListTotals({
    invoices: costRows,
    shareFor,
    levelOf: buildingId ? levelOf : undefined,
  });

  // Who may change which row (see costRowAccess).
  const unitIdsOfRows = React.useMemo(
    () =>
      scope === "group"
        ? [
            ...new Set(
              costRows
                .filter(row => levelOf(row) === "unit")
                .map(row => String(row.unitId)),
            ),
          ]
        : [],
    [scope, costRows, groupId],
  );
  const unitEditAccess = useCanEditCostsOf(unitIdsOfRows);
  const isUnitArchived = (id?: string) =>
    !!groupUnits.find(unit => String(unit.id) === id)?.archivedOn;
  const rowAccess = (row: Invoice) =>
    costRowAccess({
      page: scope,
      level: levelOf(row),
      isBuildingCostOnUnit: scope === "unit" && parentGroupCostIds.has(row.id),
      isPageArchived: isArchived,
      canEditPage: canEditProperty,
      canEditBuilding: canSettleParentGroup,
      canEditUnit: unitEditAccess.canEditOf(row.unitId),
      isUnitArchived: isUnitArchived(row.unitId),
    });
  const isRowEditable = (row: Invoice) => rowAccess(row).isEditable;
  const editOnBuilding = (row: Invoice) =>
    parentGroupId && rowAccess(row).editOnBuilding
      ? () =>
          navigate(
            `/properties/${parentGroupId}/costs?propertyCostEditId=${encodeURIComponent(
              row.id,
            )}`,
          )
      : undefined;

  // "Wijzigen op het gebouw" on a unit page lands here with the cost to
  // edit, once the rights to change it are known.
  const isLoadingEditRights =
    unitEditAccess.isLoading || pageEditPermission.isLoading;
  useEffect(() => {
    if (!drawerParams.propertyCostEditId || isLoadingEditRights) return;
    const target = invoices.find(i => i.id === drawerParams.propertyCostEditId);
    if (target) {
      if (isRowEditable(target)) setEditInvoice(target);
      setDrawerParams({ propertyCostEditId: undefined });
    }
  }, [
    drawerParams.propertyCostEditId,
    invoices,
    setDrawerParams,
    isArchived,
    isLoadingEditRights,
  ]);

  // Detail opent als sidebar-drawer (zelfde patroon als betaaldetails), niet
  // als aparte pagina.
  const openSettlement = (s: Settlement) => {
    setDrawerParams({ settlementId: s.id });
  };

  const handleDelete = (inv: Invoice) => {
    // A group cost created before the verdeelsleutel moved to the afrekening is
    // stored as one child record per unit sharing a `groupCostRef`, while the
    // group list rolls them up into a single row. Delete every sibling in one
    // go — otherwise each click removes one child and the row reappears with a
    // smaller amount (4 units = 4 clicks).
    const targets = inv.groupCostRef
      ? invoices.filter(i => i.groupCostRef === inv.groupCostRef)
      : [inv];
    // An OPEN payment invitation or order is tenant-facing: someone may be
    // about to pay a cost that would no longer exist. So deletion is blocked
    // until the broker consciously removes that payment under Betalingen —
    // the old flow warned "clean up yourself afterwards" and then deleted
    // anyway, leaving the tenant with a dangling open payment. Splits that
    // are already PAID don't block: there is nothing open to withdraw, and
    // the payment history stays under Betalingen. Judged by the live payment
    // status; the server has the last word (deleteRefusedMessage below).
    const isGroupCost = targets.some(target => target.scope === "common");
    if (
      costsHaveOpenPayment({ invoices: targets, statusMaps: costStatusMaps })
    ) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: openPaymentMessage({ kind: "cost", isGroup: isGroupCost }),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    const doDelete = async () => {
      try {
        await Promise.all(targets.map(t => remove(t.id)));
        refetch();
      } catch (error) {
        // Chained after the confirm dialog that is still closing.
        setTimeout(() =>
          confirm({
            type: "warning",
            title: getLocalizedText("costs.delete.title"),
            info: deleteRefusedMessage(error, "cost", {
              isGroup: isGroupCost,
            }),
            secondaryAction: {
              title: getLocalizedText("payment_request.button.ok"),
            },
          }),
        );
      }
    };
    // Product rule (Lewis, 2026-08-30): a cost inside a confirmed settlement
    // cannot be deleted — the settlement's frozen totals would diverge from
    // its cost list. Delete the settlement first, then the cost. Together
    // with the edit lock this makes a confirmed settlement untouchable.
    if (targets.some(t => settledInfoFor(t).settled)) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: getLocalizedText("costs.delete.blocked_settled"),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    // A departure settlement froze a tenant's share of the cost: it goes
    // first, the cost after it.
    const [departure] = targets.flatMap(
      t => settledInfoFor(t).departures ?? [],
    );
    if (departure) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: getLocalizedText("costs.delete.blocked_departure", {
          name: departure.settlementName,
        }),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    confirm({
      title: getLocalizedText("costs.delete.title"),
      info: getLocalizedText("costs.delete.confirm", {
        category: CATEGORY_LABELS_NL[inv.category],
        supplier: inv.supplier,
      }),
      primaryActions: [
        {
          title: getLocalizedText("costs.delete.title"),
          onPress: doDelete,
        },
      ],
    });
  };

  const handleDeleteSettlement = (s: Settlement) => {
    // Same rule as deleting a cost: an OPEN payment order or request created
    // from this settlement is tenant-facing, so the broker withdraws it under
    // Betalingen first — only then can the settlement itself be deleted. Paid
    // ones don't block.
    // A group settlement names each open payment and its unit. A settlement
    // with a payment is never deleted (decision of 2026-10-06): it is final,
    // a correction is a new cost in a later settlement.
    const isGroup = s.scope === "group";
    const blocked = settlementDeleteBlockedMessage({
      settlement: s,
      statusMaps: settlementStatusMaps,
    });
    if (blocked) {
      confirm({
        type: "warning",
        title: getLocalizedText("settlement.delete.title"),
        info: blocked,
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    const doDelete = async () => {
      try {
        await removeSettlement(s.id);
      } catch (error) {
        // Chained after the confirm dialog that is still closing.
        setTimeout(() =>
          confirm({
            type: "warning",
            title: getLocalizedText("settlement.delete.title"),
            info: deleteRefusedMessage(error, "settlement", { isGroup }),
            secondaryAction: {
              title: getLocalizedText("payment_request.button.ok"),
            },
          }),
        );
      }
    };
    confirm({
      title: getLocalizedText("settlement.delete.title"),
      info: getLocalizedText("settlement.delete.confirm"),
      primaryActions: [
        {
          title: getLocalizedText("costs.action.delete"),
          onPress: doDelete,
        },
      ],
    });
  };

  // Geen concepten: de lijst toont alleen opgeslagen (bevestigde) afrekeningen.
  // On a unit inside a group: the group's settlements that bill this unit's
  // tenants or its owner. They are made (and deleted) on the group, but
  // without them the unit's page showed "no settlements" while its tenant had
  // a payment request from one.
  // See listedSettlementsOf: on a building also its units' settlements and
  // departures (Q6), on a unit also its building's that bill it.
  const listedSettlements = React.useMemo(
    () =>
      listedSettlementsOf({
        page: scope,
        own: settlements,
        crossLevel: crossLevelSettlements,
        groupId,
        unitId,
        parentGroupId,
        unitIds: groupChildIds,
      }),
    [
      scope,
      settlements,
      crossLevelSettlements,
      groupId,
      unitId,
      parentGroupId,
      groupChildIds,
    ],
  );

  // Panden waarop de betaaluitnodigingen van deze afrekeningen leven (de
  // huurder-units) — voor de live betaalstatus per afrekening. On a unit, the
  // parent group too: a group settlement's bulk invitations can live there,
  // so the unit reads the same status as the group page. Plus the units each
  // settlement froze for its tenants: an invitation stays on its unit after
  // that unit is archived, and a unit page sums up a building's settlement
  // for all its tenants, as the drawer does.
  const settlementPropertyIds = React.useMemo(
    () =>
      scope === "group"
        ? paymentRequestPropertyIds({
            propertyIds: groupChildIds,
            settlements: listedSettlements,
          })
        : paymentRequestPropertyIds({
            propertyIds: [unitId, parentGroupId],
            settlements: listedSettlements,
          }),
    [scope, groupChildIds, unitId, parentGroupId, listedSettlements],
  );
  const settlementStatusMaps = useSettlementPaymentStatus(
    listedSettlements,
    settlementPropertyIds,
  );

  // Every row sums up the whole settlement, as its drawer shows it, also a
  // building's settlement on one of its units.
  const settlementRowFigures = (s: Settlement) => ({
    payStatus: settlementTenantPaymentStatus(s, settlementStatusMaps),
    amount: settlementAmountTexts({ settlement: s, format: formatMoney }),
  });

  const settlementRows: SettlementRowVM[] = listedSettlements.map(s => ({
    id: s.id,
    ...settlementRowTexts(s),
    // Inside a building "Gebouw" or "Privaat" (the unit's address in its
    // tooltip) in the Type column; a unit on its own has none.
    level: settlementRowLevel({
      settlement: s,
      buildingId,
      unitAddressOf,
    }),
    ...settlementRowFigures(s),
    // Opens in the drawer, read-only when it is not the page's own.
    onOpen: () => openSettlement(s),
    // A building's settlement is deleted on the building, a unit's on the
    // unit.
    onDelete:
      canEdit &&
      isPageOwnSettlement({
        settlement: s,
        page: scope,
        propertyId: scope === "group" ? groupId : unitId,
      })
        ? () => handleDeleteSettlement(s)
        : undefined,
  }));

  // A failed fetch replaces the list (see getListState): its rows, its count
  // are not to be trusted.
  const costsListState = getListState({
    isLoading: isLoadingInvoices,
    loadFailed: invoicesLoadFailed,
    count: costRows.length,
  });
  const hasCostRows = costsListState === "list" && costRows.length > 0;

  const renderCosts = () => {
    switch (costsListState) {
      case "load_failed":
        return (
          <ListLoadFailed
            heading={getLocalizedText("costs.overview.load_failed_costs")}
            icon="billSplit"
            onRetry={refetch}
          />
        );
      case "loading":
        return (
          <EmptyState
            heading={getLocalizedText("costs.overview.loading_costs")}
            icon="billSplit"
          />
        );
      case "empty":
        return (
          <EmptyState
            heading={getLocalizedText("costs.overview.no_costs")}
            icon="billSplit"
          >
            {canEdit ? getLocalizedText("costs.overview.no_costs_body") : null}
          </EmptyState>
        );
      default:
        return (
          <CostsTable
            invoices={costRows}
            statusByOrderId={costStatusByOrderId}
            rowLevelOf={buildingId ? rowLevelOf : undefined}
            settledInfoFor={settledInfoFor}
            onView={openCostDrawer}
            onEdit={setEditInvoice}
            onDelete={handleDelete}
            isRowEditable={isRowEditable}
            editElsewhere={editOnBuilding}
            shareFor={shareFor}
            shareUnitLabel={unitLabelInBuilding}
            onViewChange={handleCostsViewChange}
          />
        );
    }
  };

  const renderSettlements = () => {
    switch (
      getListState({
        isLoading: isLoadingSettlements,
        loadFailed: settlementsLoadFailed,
        count: listedSettlements.length,
      })
    ) {
      case "load_failed":
        return (
          <ListLoadFailed
            heading={getLocalizedText("costs.overview.load_failed_settlements")}
            icon="billStack"
            onRetry={handleRetrySettlements}
          />
        );
      case "loading":
        return (
          <EmptyState
            heading={getLocalizedText("costs.overview.loading_settlements")}
            icon="billStack"
          />
        );
      case "empty":
        return (
          <EmptyState
            heading={getLocalizedText("costs.overview.no_settlements")}
            icon="billStack"
          >
            {canEdit
              ? getLocalizedText("costs.overview.no_settlements_body")
              : null}
          </EmptyState>
        );
      default:
        return (
          <SettlementsTable
            rows={settlementRows}
            page={{ inBuilding: !!buildingId, acrossProperties: false }}
          />
        );
    }
  };

  return (
    <Stack vertical spacing="loose">
      <Card
        heading={getLocalizedText("costs.overview.costs")}
        subHeading={(() => {
          if (!hasCostRows) return undefined;
          // With a search or filter active: what the list shows of all.
          const shown = costsView?.isFiltered ? costsView : costTotals;
          const countText = costsView?.isFiltered
            ? getLocalizedText("costs.overview.count_filtered_of_total", {
                count: String(costsView.count),
                all: String(costRows.length),
                total: formatMoney(costsView.totalExcl),
              })
            : getLocalizedText(
                costTotals.count === 1
                  ? "costs.overview.count_total_excl_vat_one"
                  : "costs.overview.count_total_excl_vat_many",
                {
                  count: String(costTotals.count),
                  total: formatMoney(costTotals.totalExcl),
                },
              );
          // Q3: building costs count at the unit's share; on a unit page
          // the line says what they add ("waarvan gebouw € 578,00"), and
          // those whose share the settlement still has to work out are
          // named.
          return [
            countText,
            unitLabelInBuilding != null && shown.buildingTotalExcl !== 0
              ? getLocalizedText("costs.overview.of_which_building", {
                  amount: formatMoney(shown.buildingTotalExcl),
                })
              : undefined,
            shown.pendingShareCount > 0
              ? getLocalizedText(
                  shown.pendingShareCount === 1
                    ? "costs.overview.shares_pending_one"
                    : "costs.overview.shares_pending_many",
                  { count: String(shown.pendingShareCount) },
                )
              : undefined,
          ]
            .filter(Boolean)
            .join(" · ");
        })()}
        primaryActions={[
          ...(canEdit
            ? [
                {
                  content: getLocalizedText("costs.action.add_cost"),
                  onClick: () => setShowModal(true),
                },
              ]
            : []),
        ]}
      >
        {renderCosts()}
      </Card>
      {showNewSettlement && (
        <NewSettlementModal
          candidates={departureLeases}
          isInBuilding={unitLabelInBuilding != null}
          onChooseBuilding={
            startBuildingSettlement
              ? () => {
                  setShowNewSettlement(false);
                  startBuildingSettlement();
                }
              : undefined
          }
          onChoose={departureContractId => {
            setShowNewSettlement(false);
            startSettlement(departureContractId);
          }}
          onClose={() => setShowNewSettlement(false)}
        />
      )}

      <Card
        heading={getLocalizedText("costs.overview.settlements")}
        primaryActions={
          canEdit
            ? [
                {
                  content: getLocalizedText("costs.overview.add_settlement"),
                  onClick: () => {
                    // A unit inside a building, or with a tenant who leaves
                    // or left, first asks which kind of settlement.
                    if (
                      unitLabelInBuilding != null ||
                      departureLeases.length > 0
                    ) {
                      setShowNewSettlement(true);
                      return;
                    }
                    startSettlement();
                  },
                },
              ]
            : []
        }
      >
        {renderSettlements()}
      </Card>

      {showModal && (
        <InvoiceModal
          properties={assignable}
          // Inside a unit or building the cost is implied → skip assignment step.
          fixedPropertyId={scope === "group" ? groupId : unitId}
          existingInvoices={invoices}
          onClose={() => setShowModal(false)}
          onSaveMany={async payloads => {
            const stored: Invoice[] = [];
            for (const p of payloads) {
              // Group-cost children carry their own real unitId + groupCostRef —
              // don't rewrite them onto the group. Only tag plain group-scoped
              // costs (no groupCostRef, no unitId) to the group.
              const payload =
                scope === "group" && !p.groupCostRef && !p.unitId
                  ? { ...p, groupId, unitId: undefined }
                  : p;
              // eslint-disable-next-line no-await-in-loop
              stored.push(await create(payload));
            }
            refetch();
            return stored;
          }}
          // The popup adds every later tenant's payment to the cost it stored.
          onUpdate={async (id, payload) => {
            await update(id, payload);
            refetch();
          }}
        />
      )}

      {editInvoice && (
        <InvoiceModal
          properties={assignable}
          initialInvoice={editInvoice}
          departureShares={settledInfoFor(editInvoice).departures}
          onClose={() => setEditInvoice(null)}
          onSaveMany={() => undefined}
          onUpdate={async (id, payload) => {
            await update(id, payload);
            refetch();
          }}
        />
      )}
    </Stack>
  );
};
